import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";
import type { TrackWeek, UserPreferences } from "../types";
import { api } from "../utils/api";

interface TrackTempPrediction {
  valueF: number; valueC: number;
  lowF: number; highF: number; lowC: number; highC: number;
  confidence: string;
  correctionApplied: boolean;
  observationCount: number;
}
interface AccuracyRun {
  sessionDate: string | null;
  projectedF: number;
  actualF: number;
  errorF: number; // actual - projected (negative = ran cooler than projected)
}
interface TrackAccuracy {
  trackId: number | null;
  n: number;
  meanAbsErrorF: number | null;
  hasRecord: boolean;
  minObs: number;
  recent: AccuracyRun[];
}
interface ManualCheck {
  id: string;
  projectedF: number;
  actualF: number;
  errorF: number; // actual - projected
  sessionTimeLabel: string | null;
  createdAt: string | null;
}
import {
  buildWeatherShareText,
  estimateProjectedTrackTemp,
  formatDateRange,
  formatPrecipChance,
  formatProjectedCloud,
  formatProjectedHumidity,
  formatProjectedTemp,
  formatProjectedWind,
  formatScheduleTempRange,
} from "../utils/scheduleWeather";

interface Props {
  week: TrackWeek | null | undefined;
  units?: UserPreferences["units"];
}

export default function TrackWeekWeatherCard({ week, units = "standard" }: Props) {
  const weather = week?.scheduleWeather || null;
  const [copyState, setCopyState] = useState<"idle" | "copied" | "error">("idle");
  const [downloadState, setDownloadState] = useState<"idle" | "saving" | "done" | "error">("idle");
  const exportRef = useRef<HTMLDivElement>(null);
  const shareRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (copyState === "idle") return;
    const timer = window.setTimeout(() => setCopyState("idle"), 1800);
    return () => window.clearTimeout(timer);
  }, [copyState]);

  useEffect(() => {
    if (downloadState === "idle" || downloadState === "saving") return;
    const timer = window.setTimeout(() => setDownloadState("idle"), 1800);
    return () => window.clearTimeout(timer);
  }, [downloadState]);

  // Backend track-temp prediction (#31): physics baseline + observation calibration.
  // Falls back to the local estimate below on error / while loading.
  const [prediction, setPrediction] = useState<TrackTempPrediction | null>(null);
  useEffect(() => {
    const id = week?.id;
    if (!id) {
      setPrediction(null);
      return;
    }
    let cancelled = false;
    api<TrackTempPrediction>(`/api/strategy/track-temp/prediction?trackWeekId=${id}`)
      .then((p) => !cancelled && setPrediction(p))
      .catch(() => !cancelled && setPrediction(null));
    return () => {
      cancelled = true;
    };
  }, [week?.id]);

  // #283 "track record": the team's own past projected-vs-actual runs at this track,
  // surfacing the loop the backend already runs on every upload. Read-only; hidden
  // entirely on error / no history.
  const [accuracy, setAccuracy] = useState<TrackAccuracy | null>(null);
  const [showRuns, setShowRuns] = useState(false);
  useEffect(() => {
    const id = week?.id;
    setShowRuns(false);
    if (!id) {
      setAccuracy(null);
      return;
    }
    let cancelled = false;
    api<TrackAccuracy>(`/api/strategy/track-temp/accuracy?trackWeekId=${id}`)
      .then((a) => !cancelled && setAccuracy(a))
      .catch(() => !cancelled && setAccuracy(null));
    return () => {
      cancelled = true;
    };
  }, [week?.id]);

  // #283 Slice 2 — the team's logged manual guess-then-checks for this week. These
  // are ASSERTIONS (human-typed), shown separately from the telemetry strip above
  // and never used to train the projection.
  const [manualChecks, setManualChecks] = useState<ManualCheck[]>([]);
  const [showLog, setShowLog] = useState(false);
  const [actualInput, setActualInput] = useState("");
  const [logState, setLogState] = useState<"idle" | "saving" | "error">("idle");
  const [logError, setLogError] = useState("");
  // Tracks the currently-shown week so an in-flight submit can't write its result
  // into a different week's state if the user navigates away mid-request.
  const weekIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const id = week?.id;
    weekIdRef.current = id;
    setShowLog(false);
    setActualInput("");
    setLogState("idle");
    setLogError("");
    if (!id) {
      setManualChecks([]);
      return;
    }
    let cancelled = false;
    api<{ checks: ManualCheck[] }>(`/api/strategy/track-temp/manual-checks?trackWeekId=${id}`)
      .then((r) => !cancelled && setManualChecks(r.checks))
      .catch(() => !cancelled && setManualChecks([]));
    return () => {
      cancelled = true;
    };
  }, [week?.id]);

  const submitManualCheck = useCallback(async () => {
    const id = week?.id;
    if (!id) return;
    const raw = parseFloat(actualInput);
    if (Number.isNaN(raw)) {
      setLogState("error");
      setLogError("Enter the track temp you saw in-sim");
      return;
    }
    // The input is in the user's unit; the API stores °F.
    const actualF = units === "metric" ? raw * 1.8 + 32 : raw;
    setLogState("saving");
    setLogError("");
    try {
      // The POST returns the created row, so prepend it (recent-first) instead of a
      // second round-trip. Guard against a week switch mid-request.
      const created = await api<ManualCheck>(
        `/api/strategy/track-temp/manual-check?trackWeekId=${id}`,
        { method: "POST", body: JSON.stringify({ actualF }) },
      );
      if (weekIdRef.current !== id) return;
      setManualChecks((prev) => [created, ...prev]);
      setActualInput("");
      setShowLog(false);
      setLogState("idle");
    } catch (e) {
      setLogState("error");
      setLogError((e as Error)?.message || "Could not save the check");
    }
  }, [week?.id, actualInput, units]);

  const shareText = useMemo(() => {
    if (!week || !weather) return "";
    return buildWeatherShareText(week, weather, units);
  }, [week, weather, units]);

  const handleDownload = useCallback(async () => {
    const el = shareRef.current;
    if (!el) return;
    setDownloadState("saving");
    try {
      // html2canvas captures live-DOM layout, so the fonts and the series badge
      // must be fully loaded BEFORE capture — otherwise the first cold-cache
      // download silently falls back to system-ui (engineer's hard requirement).
      if (document.fonts) {
        await document.fonts.ready;
        await Promise.all([
          document.fonts.load('700 200px "Geist"'),
          document.fonts.load('600 30px "Geist"'),
          // Probe at the title's real size (50px) so a cold cache warms the same
          // metrics html2canvas captures — a mismatched probe size could let the
          // first export fall back to the system serif (review-council catch).
          document.fonts.load('600 50px "Fraunces Variable"'),
        ]).catch(() => undefined);
      }
      const badge = el.querySelector("img");
      if (badge && !badge.complete) {
        await badge.decode().catch(() => undefined);
      }
      const { default: html2canvas } = await import("html2canvas");
      // Fixed 1080-square artboard → deterministic geometry, captured at 2x → 2160² PNG.
      const canvas = await html2canvas(el, {
        backgroundColor: "#0f1217",
        logging: false,
        scale: 2,
        useCORS: true,
        width: 1080,
        height: 1080,
        windowWidth: 1080,
        windowHeight: 1080,
      });
      const link = document.createElement("a");
      link.download = `weather-${week?.trackName?.replace(/\s+/g, "-") || "card"}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      setDownloadState("done");
    } catch {
      setDownloadState("error");
    }
  }, [week]);

  if (!week || !weather) {
    return null;
  }

  const projectedTemp =
    formatProjectedTemp(weather.controlWeather, units) ||
    formatScheduleTempRange(weather.officialForecast, units) ||
    "Forecast pending";
  const projectedCloud =
    formatProjectedCloud(weather.controlWeather, weather.officialForecast) || "Forecast pending";
  const projectedHumidity =
    formatProjectedHumidity(weather.controlWeather, weather.officialForecast) || "--";
  const projectedWind = formatProjectedWind(weather.controlWeather, units) || "--";
  const projectedTrackEstimate = estimateProjectedTrackTemp(
    weather.controlWeather,
    weather.sessionTimeLabel,
    week.startsAt,
  );
  // Prefer the backend prediction (calibrated as observations accrue); fall back
  // to the local physics estimate while loading or on error.
  const calibNote = prediction
    ? prediction.correctionApplied
      ? ` · calibrated (${prediction.observationCount} ${prediction.observationCount === 1 ? "run" : "runs"})`
      : " · physics baseline"
    : "";
  const trackTempValue = prediction
    ? units === "metric"
      ? `${prediction.valueC}°C`
      : `${prediction.valueF}°F`
    : projectedTrackEstimate
      ? units === "metric"
        ? `${projectedTrackEstimate.valueC}°C`
        : `${projectedTrackEstimate.valueF}°F`
      : "--";
  const trackTempDetail = prediction
    ? units === "metric"
      ? `${prediction.lowC}–${prediction.highC}°C · ${prediction.confidence}${calibNote}`
      : `${prediction.lowF}–${prediction.highF}°F · ${prediction.confidence}${calibNote}`
    : projectedTrackEstimate
      ? units === "metric"
        ? `${projectedTrackEstimate.lowC}–${projectedTrackEstimate.highC}°C · ${projectedTrackEstimate.confidence}`
        : `${projectedTrackEstimate.lowF}–${projectedTrackEstimate.highF}°F · ${projectedTrackEstimate.confidence}`
      : undefined;
  // #283 trust-strip derivations. Errors are deltas (°F/1.8 for °C); absolute
  // temps convert (°F−32)/1.8. Honest cold-start: no band claim below minObs.
  const isMetric = units === "metric";
  const unitSuffix = isMetric ? "°C" : "°F";
  const fmtAbs = (f: number) => `${Math.round(isMetric ? (f - 32) / 1.8 : f)}${unitSuffix}`;
  const fmtDelta = (f: number) => {
    const v = Math.round((isMetric ? f / 1.8 : f) * 10) / 10;
    return `${v > 0 ? "+" : ""}${v}${unitSuffix}`;
  };
  const accuracyBand =
    accuracy?.meanAbsErrorF != null
      ? Math.round((isMetric ? accuracy.meanAbsErrorF / 1.8 : accuracy.meanAbsErrorF) * 10) / 10
      : null;
  const fmtRunDate = (iso: string | null) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
      ? "—"
      : d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  };

  const precipitation = formatPrecipChance(weather.officialForecast) || "None";
  const weekLabel = week.displayWeekNumber ?? week.weekNumber;
  const dateLabel = formatDateRange(week.startsAt, week.endsAt);

  // ── shareable-image derivations (#276) ──────────────────────────────────
  // The hero of the share card is the calibrated TRACK-TEMP prediction — the one
  // value a weather app can't produce. The "Calibrated · N runs" / "Physics
  // baseline" proof line is the credibility signal, not a footnote.
  const tRange = prediction
    ? units === "metric"
      ? `${prediction.lowC}–${prediction.highC}°C`
      : `${prediction.lowF}–${prediction.highF}°F`
    : projectedTrackEstimate
      ? units === "metric"
        ? `${projectedTrackEstimate.lowC}–${projectedTrackEstimate.highC}°C`
        : `${projectedTrackEstimate.lowF}–${projectedTrackEstimate.highF}°F`
      : null;
  const tCalibrated = !!prediction?.correctionApplied;
  const tRuns = prediction?.observationCount ?? 0;
  const tProof = prediction
    ? tCalibrated
      ? `Calibrated · ${tRuns} ${tRuns === 1 ? "run" : "runs"}`
      : "Physics baseline"
    : "Physics estimate";
  // Square confidence dot: green once observations calibrate it, amber on the
  // bare physics baseline. Semantic, not decorative.
  const tDotColor = tCalibrated ? "#4ade80" : "#facc15";
  const tConfidence = prediction?.confidence ?? projectedTrackEstimate?.confidence ?? null;
  const precipActive = !/^(none|0%?|--|—)$/i.test(precipitation.trim());
  const shareEyebrow = [week.seriesName, weekLabel != null ? `Week ${weekLabel}` : null, dateLabel]
    .filter(Boolean)
    .join("  ·  ");
  const ledgerCells: { label: string; value: string; dot?: string }[] = [
    { label: "Air temp", value: projectedTemp },
    { label: "Sky", value: projectedCloud },
    { label: "Session time", value: weather.sessionTimeLabel || "--" },
    { label: "Wind", value: projectedWind },
    { label: "Precip", value: precipitation, dot: precipActive ? "#facc15" : undefined },
    { label: "Humidity", value: projectedHumidity },
  ];

  const handleCopy = async () => {
    if (!shareText) return;
    try {
      // navigator.clipboard requires a secure context (HTTPS or localhost).
      // The on-prem VM is served over plain http, so fall back to execCommand.
      if (window.isSecureContext && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareText);
      } else {
        const ta = document.createElement("textarea");
        ta.value = shareText;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.top = "0";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        if (!ok) throw new Error("copy command rejected");
      }
      setCopyState("copied");
    } catch {
      setCopyState("error");
    }
  };

  return (
    <section className="card" style={styles.shell}>
      <div style={styles.actions}>
        <button type="button" className="btn btn-secondary" onClick={handleCopy}>
          {copyState === "copied"
            ? "Copied"
            : copyState === "error"
              ? "Copy failed"
              : "Copy Summary"}
        </button>
        <button type="button" className="btn btn-secondary" onClick={handleDownload}>
          {downloadState === "saving"
            ? "Saving..."
            : downloadState === "done"
              ? "Saved"
              : downloadState === "error"
                ? "Failed"
                : "Download Image"}
        </button>
      </div>

      <div ref={exportRef} style={styles.exportPanel}>
        <div style={styles.header}>
          <div style={styles.headerMain}>
            <div style={styles.kicker}>Projected weather</div>
            <div style={styles.trackName}>{week.trackName}</div>
            <div style={styles.metaRow}>
              {week.seriesName && <span style={styles.exportPill}>{week.seriesName}</span>}
              {weekLabel != null && <span style={styles.exportPill}>Week {weekLabel}</span>}
              {dateLabel && <span style={styles.exportPill}>{dateLabel}</span>}
            </div>
          </div>
        </div>

        <div style={styles.mainGrid}>
          <div style={styles.heroPanel}>
            <div style={styles.heroEyebrow}>Air Temp</div>
            <div style={styles.heroTemp}>{projectedTemp}</div>
            <div style={styles.heroCondition}>{projectedCloud}</div>
            <div style={styles.heroSummary}>
              {projectedHumidity} humidity | {projectedWind}
            </div>
          </div>

          <div style={styles.metricGrid}>
            <MetricCard
              label="Track Temperature"
              value={trackTempValue}
              detail={trackTempDetail}
            />
            <MetricCard label="Time" value={weather.sessionTimeLabel || "--"} />
            <MetricCard label="Humidity" value={projectedHumidity} />
            <MetricCard label="Wind" value={projectedWind} />
            <MetricCard label="Precipitation" value={precipitation} />
          </div>
        </div>

        {/* #283 Track record — surfaces the existing projected-vs-actual loop.
            On-screen only (NOT in the share artboard below). Hidden when no history. */}
        {accuracy && accuracy.n > 0 && (
          <div style={styles.trackRecord}>
            <div style={styles.trackRecordHead}>
              <span
                style={{
                  ...styles.trackRecordDot,
                  background: accuracy.hasRecord ? "var(--flag-green)" : "var(--flag-yellow)",
                }}
              />
              <span style={styles.trackRecordText}>
                {accuracy.hasRecord && accuracyBand != null ? (
                  <>
                    Track record: projected within{" "}
                    <strong style={styles.trackRecordStrong}>±{accuracyBand}{unitSuffix}</strong> over your
                    last {accuracy.recent.length} {accuracy.recent.length === 1 ? "run" : "runs"} here
                  </>
                ) : (
                  <>
                    Still learning — <strong style={styles.trackRecordStrong}>{accuracy.n}/{accuracy.minObs}</strong>{" "}
                    {accuracy.n === 1 ? "run" : "runs"} logged here
                  </>
                )}
              </span>
              <button
                type="button"
                style={styles.trackRecordToggle}
                onClick={() => setShowRuns((v) => !v)}
                aria-expanded={showRuns}
                aria-controls="track-record-runs"
              >
                {showRuns ? "Hide runs" : "View runs"}
              </button>
            </div>

            {showRuns && (
              <div id="track-record-runs" style={styles.trackRecordList}>
                {accuracy.recent.map((run, i) => (
                  <div key={i} style={styles.trackRecordRow}>
                    <span style={styles.trackRecordDate}>{fmtRunDate(run.sessionDate)}</span>
                    <span style={styles.trackRecordPair}>
                      proj {fmtAbs(run.projectedF)} · actual {fmtAbs(run.actualF)}
                    </span>
                    <span
                      style={{
                        ...styles.trackRecordDelta,
                        // 3°F is a fixed physical tolerance (intentionally °F-anchored,
                        // not converted) for the in-band green cue.
                        color: Math.abs(run.errorF) <= 3 ? "var(--flag-green)" : "var(--text-secondary)",
                      }}
                    >
                      {fmtDelta(run.errorF)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* #283 Slice 2 — deliberate guess-then-check. ASSERTIONS (human-typed),
            shown with a distinct slate dot + caption so they are never mistaken for
            the telemetry track record above. On-screen only; never trains the model. */}
        <div style={styles.manualCheck}>
          <div style={styles.manualCheckHead}>
            <span style={styles.manualCheckTitle}>Check the projection</span>
            {!showLog && (
              <button
                type="button"
                style={styles.trackRecordToggle}
                onClick={() => setShowLog(true)}
                aria-expanded={showLog}
                aria-controls="manual-check-form"
              >
                Log actual temp
              </button>
            )}
          </div>

          {showLog ? (
            <div id="manual-check-form" style={styles.manualForm}>
              <label style={styles.manualFormLabel} htmlFor="manual-actual">
                Actual track temp at session start ({unitSuffix})
              </label>
              <div style={styles.manualFormRow}>
                <input
                  id="manual-actual"
                  style={styles.manualInput}
                  type="number"
                  inputMode="decimal"
                  value={actualInput}
                  placeholder={isMetric ? "e.g. 42" : "e.g. 108"}
                  onChange={(e) => setActualInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") void submitManualCheck();
                  }}
                />
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={logState === "saving"}
                  onClick={() => void submitManualCheck()}
                >
                  {logState === "saving" ? "Saving…" : "Save"}
                </button>
                <button
                  type="button"
                  style={styles.trackRecordToggle}
                  onClick={() => {
                    setShowLog(false);
                    setLogError("");
                  }}
                >
                  Cancel
                </button>
              </div>
              {logError && <div style={styles.manualError}>{logError}</div>}
              <div style={styles.manualNote}>Your check — never trains the projection.</div>
            </div>
          ) : (
            manualChecks.length === 0 && (
              <div style={styles.manualNote}>
                Read the track temp in-sim at green, then log it to check the projection.
              </div>
            )
          )}

          {manualChecks.length > 0 && (
            <div style={styles.trackRecordList}>
              {/* Persistent trust-class caption — the "never trains" note must survive
                  the form being collapsed, not lean on the slate dot alone. */}
              <div style={styles.manualNote}>Your checks — display only, never train the projection.</div>
              {manualChecks.map((c) => (
                <div key={c.id} style={styles.trackRecordRow}>
                  <span style={styles.manualDot} aria-label="Manual check (not telemetry)" />
                  <span style={styles.trackRecordPair}>
                    proj {fmtAbs(c.projectedF)} · actual {fmtAbs(c.actualF)}
                  </span>
                  {/* Neutral delta — the green "accurate" cue stays exclusive to the
                      telemetry track record above; an assertion never wears it. */}
                  <span style={{ ...styles.trackRecordDelta, color: "var(--text-secondary)" }}>
                    {fmtDelta(c.errorF)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Off-screen 1080×1080 share artboard — captured by Download Image (#276).
          Deliberately NOT the on-screen card: a fixed-pixel instrument poster with
          the calibrated track-temp prediction as the hero. All px, no rem/clamp, so
          html2canvas geometry is deterministic across viewports. */}
      <div ref={shareRef} style={styles.shareArtboard} aria-hidden="true">
        <div style={styles.shareMasthead}>
          <div style={styles.shareBrandMark}>
            <span style={styles.shareBrandX}>X</span>
          </div>
          <div style={styles.shareBrandCopy}>
            <span style={styles.shareBrandEyebrow}>Project</span>
            <span style={styles.shareBrandName}>StintSense</span>
          </div>
          <span style={styles.shareMastheadTag}>Projected weather</span>
        </div>

        <div style={styles.shareRule} />

        <div style={styles.shareIdentity}>
          <div style={styles.shareEyebrow}>{shareEyebrow}</div>
          <div style={styles.shareTrackRow}>
            {week.seriesLogoUrl && (
              <img src={week.seriesLogoUrl} alt="" style={styles.shareSeriesBadge} />
            )}
            <div style={styles.shareTrackName}>{week.trackName}</div>
          </div>
        </div>

        <div style={styles.shareHero}>
          <div style={styles.shareHeroEyebrow}>Projected track temperature</div>
          <div style={styles.shareHeroValue}>{trackTempValue}</div>
          <div style={styles.shareProof}>
            <span style={{ ...styles.shareDot, background: tDotColor }} />
            {tRange && <span style={styles.shareProofRange}>{tRange}</span>}
            {tRange && <span style={styles.shareProofSep}>·</span>}
            <span style={styles.shareProofNote}>{tProof}</span>
          </div>
        </div>

        <div style={{ ...styles.shareRule, marginTop: "auto" }} />

        <div style={styles.shareLedger}>
          {ledgerCells.map((cell, i) => {
            const col = i % 3;
            const row = Math.floor(i / 3);
            return (
              <div
                key={cell.label}
                style={{
                  ...styles.shareCell,
                  ...(col > 0 ? styles.shareCellLeftRule : null),
                  ...(row > 0 ? styles.shareCellTopRule : null),
                }}
              >
                <div style={styles.shareCellLabel}>{cell.label}</div>
                <div style={styles.shareCellValue}>
                  {cell.dot && <span style={{ ...styles.shareDotSm, background: cell.dot }} />}
                  {cell.value}
                </div>
              </div>
            );
          })}
        </div>

        <div style={styles.shareFooter}>
          <span style={styles.shareDomain}>projectxpr.app</span>
          {tConfidence && (
            <span style={styles.shareFooterMeta}>{tConfidence} confidence</span>
          )}
        </div>
      </div>
    </section>
  );
}

function MetricCard({
  label,
  value,
  detail,
}: {
  label: string;
  value: string;
  detail?: string;
}) {
  return (
    <div style={styles.metricCard}>
      <div style={styles.metricLabel}>{label}</div>
      <div style={styles.metricValue}>{value}</div>
      {detail && <div style={styles.metricDetail}>{detail}</div>}
    </div>
  );
}

// IMPORTANT: this panel is captured by html2canvas. Avoid clamp(), inline-flex
// vertical centering of pure text, and shared utility classes whose live-DOM
// rendering differs from html2canvas's measurement. Use fixed sizes, explicit
// line-heights, and balanced top/bottom padding so the export matches the
// preview pixel-for-pixel.
const styles: Record<string, CSSProperties> = {
  shell: {
    position: "relative",
    padding: "1.1rem",
    marginBottom: "1rem",
    display: "flex",
    flexDirection: "column",
    gap: "1rem",
    border: "1px solid rgba(148, 163, 184, 0.18)",
    background: "var(--ink-2)",
  },
  // Float the export controls into the panel's top-right (beside the kicker) instead
  // of a full-width row above it, which left a large empty band.
  actions: {
    position: "absolute",
    top: "1.5rem",
    right: "1.6rem",
    zIndex: 2,
    display: "flex",
    gap: "0.5rem",
    flexWrap: "wrap",
  },
  exportPanel: {
    padding: "1.5rem 1.6rem",
    display: "flex",
    flexDirection: "column",
    gap: "1.3rem",
    background: "var(--ink-2)",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    gap: "1rem",
    alignItems: "flex-start",
    flexWrap: "wrap",
  },
  headerMain: {
    minWidth: 0,
    flex: 1,
  },
  kicker: {
    fontFamily: "var(--font-body)",
    fontSize: "0.8125rem",
    fontWeight: 600,
    letterSpacing: "0.02em",
    color: "var(--text-muted)",
    lineHeight: 1,
    marginBottom: "0.75rem",
  },
  trackName: {
    fontFamily: "var(--font-display)",
    fontSize: "2.1rem",
    lineHeight: 1.25,
    fontWeight: 700,
    color: "var(--text-strong)",
    margin: 0,
    marginBottom: "0.95rem",
  },
  metaRow: {
    display: "flex",
    gap: "0.5rem",
    flexWrap: "wrap",
    alignItems: "center",
  },
  // Replacement for the .pill utility class — block-level layout with a
  // matching top/bottom padding pair and lineHeight: 1 keeps the glyph
  // visually centered in html2canvas (inline-flex centering does not).
  exportPill: {
    display: "inline-block",
    padding: "0.5rem 0.8rem 0.45rem",
    borderRadius: "999px",
    border: "1px solid var(--rule-strong)",
    color: "var(--text-secondary)",
    fontFamily: "var(--font-body)",
    fontSize: "0.8125rem",
    fontWeight: 500,
    lineHeight: 1,
    letterSpacing: 0,
    whiteSpace: "nowrap",
  },
  mainGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: "1rem",
    alignItems: "stretch",
  },
  heroPanel: {
    display: "flex",
    flexDirection: "column",
    padding: "1.4rem 1.3rem",
    borderRadius: "20px",
    border: "1px solid rgba(34, 197, 94, 0.16)",
    background: "var(--ink-1)",
    minHeight: "12rem",
    justifyContent: "center",
  },
  heroEyebrow: {
    fontFamily: "var(--font-body)",
    color: "var(--text-secondary)",
    fontSize: "0.82rem",
    fontWeight: 500,
    lineHeight: 1,
    marginBottom: "0.7rem",
  },
  heroTemp: {
    color: "var(--text-strong)",
    fontFamily: "var(--font-display)",
    fontSize: "3.6rem",
    fontWeight: 700,
    lineHeight: 1.1,
    fontVariantNumeric: "tabular-nums",
    margin: 0,
    marginBottom: "0.55rem",
  },
  heroCondition: {
    color: "var(--text-secondary)",
    fontSize: "1rem",
    fontWeight: 600,
    lineHeight: 1.3,
    marginBottom: "0.45rem",
  },
  heroSummary: {
    color: "var(--text-muted)",
    fontSize: "0.82rem",
    lineHeight: 1.45,
    maxWidth: "34rem",
  },
  metricGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: "0.8rem",
  },
  metricCard: {
    display: "flex",
    flexDirection: "column",
    minHeight: "5.2rem",
    padding: "0.95rem 1rem",
    borderRadius: "18px",
    border: "1px solid rgba(148, 163, 184, 0.1)",
    background: "rgba(12, 19, 31, 0.96)",
  },
  metricLabel: {
    fontFamily: "var(--font-body)",
    color: "var(--text-secondary)",
    fontSize: "0.8125rem",
    fontWeight: 500,
    lineHeight: 1,
    marginBottom: "0.45rem",
  },
  metricValue: {
    color: "var(--text-strong)",
    fontSize: "1.05rem",
    fontWeight: 700,
    lineHeight: 1.2,
    fontVariantNumeric: "tabular-nums",
  },
  metricDetail: {
    color: "var(--text-muted)",
    fontSize: "0.8125rem",
    lineHeight: 1.3,
    marginTop: "0.35rem",
  },

  // ── Track record / trust strip (#283) ────────────────────────────────────
  trackRecord: {
    display: "flex",
    flexDirection: "column",
    gap: "0.7rem",
    padding: "0.85rem 1rem",
    borderRadius: "14px",
    border: "1px solid rgba(148, 163, 184, 0.12)",
    background: "var(--ink-1)",
  },
  trackRecordHead: {
    display: "flex",
    alignItems: "center",
    gap: "0.6rem",
    flexWrap: "wrap",
  },
  trackRecordDot: {
    width: "8px",
    height: "8px",
    borderRadius: "999px",
    flexShrink: 0,
  },
  trackRecordText: {
    fontFamily: "var(--font-body)",
    color: "var(--text-secondary)",
    fontSize: "0.875rem",
    lineHeight: 1.35,
    flex: 1,
    minWidth: "12rem",
  },
  trackRecordStrong: {
    color: "var(--text-strong)",
    fontWeight: 700,
    fontVariantNumeric: "tabular-nums",
  },
  trackRecordToggle: {
    background: "transparent",
    border: "none",
    padding: 0,
    cursor: "pointer",
    color: "var(--live)",
    fontFamily: "var(--font-body)",
    fontSize: "0.8125rem",
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  trackRecordList: {
    display: "flex",
    flexDirection: "column",
    gap: "0.3rem",
    borderTop: "1px solid rgba(148, 163, 184, 0.1)",
    paddingTop: "0.6rem",
  },
  trackRecordRow: {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    fontSize: "0.8125rem",
    lineHeight: 1.3,
    color: "var(--text-muted)",
  },
  trackRecordDate: {
    width: "3.5rem",
    flexShrink: 0,
    color: "var(--text-secondary)",
    fontWeight: 600,
  },
  trackRecordPair: {
    flex: 1,
    fontVariantNumeric: "tabular-nums",
  },
  trackRecordDelta: {
    fontVariantNumeric: "tabular-nums",
    fontWeight: 700,
    whiteSpace: "nowrap",
  },

  // ── Manual guess-then-check (#283 Slice 2) — distinct from the telemetry strip ──
  manualCheck: {
    display: "flex",
    flexDirection: "column",
    gap: "0.6rem",
    padding: "0.85rem 1rem",
    borderRadius: "14px",
    border: "1px dashed rgba(148, 163, 184, 0.18)",
    background: "transparent",
  },
  manualCheckHead: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "0.6rem",
  },
  manualCheckTitle: {
    fontFamily: "var(--font-body)",
    color: "var(--text-secondary)",
    fontSize: "0.875rem",
    fontWeight: 600,
  },
  manualForm: {
    display: "flex",
    flexDirection: "column",
    gap: "0.45rem",
  },
  manualFormLabel: {
    fontFamily: "var(--font-body)",
    color: "var(--text-muted)",
    fontSize: "0.8125rem",
  },
  manualFormRow: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    flexWrap: "wrap",
  },
  manualInput: {
    width: "7rem",
    padding: "0.45rem 0.6rem",
    borderRadius: "10px",
    border: "1px solid var(--rule-strong)",
    background: "var(--ink-1)",
    color: "var(--text-strong)",
    fontFamily: "var(--font-body)",
    fontSize: "0.9rem",
    fontVariantNumeric: "tabular-nums",
  },
  manualError: {
    color: "var(--flag-yellow)",
    fontSize: "0.8125rem",
    lineHeight: 1.3,
  },
  manualNote: {
    color: "var(--text-muted)",
    fontSize: "0.8125rem",
    lineHeight: 1.35,
    fontStyle: "italic",
  },
  // Neutral slate dot — deliberately NOT the green/amber telemetry dots, so a
  // human assertion is never read as a measured run.
  manualDot: {
    width: "8px",
    height: "8px",
    borderRadius: "999px",
    flexShrink: 0,
    background: "var(--text-muted)",
  },

  // ── Share artboard (#276) ────────────────────────────────────────────────
  // Fixed 1080×1080, off-screen, ALL px (no rem/clamp/auto-fit) so html2canvas
  // captures identical geometry on every viewport. Literal hex (not var()) keeps
  // the poster deterministic and off the responsive cascade. Texture comes from
  // type scale + hairline rules + negative space — never fills/shadow/gradient.
  shareArtboard: {
    position: "fixed",
    left: "-99999px",
    top: 0,
    width: "1080px",
    height: "1080px",
    boxSizing: "border-box",
    padding: "72px",
    background: "#0f1217",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    fontFamily: '"Geist", system-ui, sans-serif',
  },
  shareMasthead: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
  },
  shareBrandMark: {
    width: "56px",
    height: "56px",
    borderRadius: "12px",
    background: "#14181f",
    border: "1px solid rgba(220, 230, 245, 0.13)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  shareBrandX: {
    fontWeight: 800,
    fontSize: "27px",
    color: "#f1f4fa",
    letterSpacing: "-0.08em",
    lineHeight: 1,
  },
  shareBrandCopy: {
    display: "flex",
    flexDirection: "column",
  },
  shareBrandEyebrow: {
    fontSize: "15px",
    fontWeight: 500,
    color: "#656c7b",
    lineHeight: 1.15,
  },
  shareBrandName: {
    fontSize: "26px",
    fontWeight: 700,
    letterSpacing: "-0.04em",
    color: "#f1f4fa",
    lineHeight: 1.1,
  },
  shareMastheadTag: {
    marginLeft: "auto",
    fontSize: "16px",
    fontWeight: 600,
    color: "#656c7b",
    letterSpacing: 0,
  },
  shareRule: {
    height: "1px",
    background: "rgba(220, 230, 245, 0.16)",
    margin: "26px 0",
  },
  shareIdentity: {
    display: "flex",
    flexDirection: "column",
  },
  shareEyebrow: {
    fontSize: "18px",
    fontWeight: 600,
    color: "#8d94a3",
    marginBottom: "14px",
    letterSpacing: 0,
  },
  shareTrackRow: {
    // flex-start, NOT center: html2canvas 1.4.1 has no real flex engine and
    // mis-measures the tall serif line box, so alignItems:center lifted the
    // captured title up over the eyebrow/badge and clipped its ascenders.
    // Top-aligning anchors the title's top edge to document flow instead.
    display: "flex",
    alignItems: "flex-start",
    gap: "20px",
  },
  shareSeriesBadge: {
    width: "58px",
    height: "58px",
    objectFit: "contain",
    flexShrink: 0,
    // 6px ≈ the cap-line center for 50px Fraunces inside the 60px line box (title
    // also has paddingTop:4px); seats the badge optically on the title's first
    // line now that the row is flex-start. Re-tune if fontSize/lineHeight/the
    // title paddingTop change.
    marginTop: "6px",
  },
  shareTrackName: {
    fontFamily: '"Fraunces Variable", "Fraunces", "Times New Roman", serif',
    fontSize: "50px",
    fontWeight: 600,
    fontVariationSettings: '"opsz" 50, "wght" 600',
    // Explicit px line box (not a ratio): html2canvas resolves ratio
    // line-heights against its own variable-font ascent metrics, which diverge
    // from the live DOM. A fixed 60px box + top pad reserves ascender headroom
    // so the cap line never clips — identical geometry for 1- and 2-line names.
    lineHeight: "60px",
    paddingTop: "4px",
    color: "#f1f4fa",
    letterSpacing: "-0.01em",
    // Names that exceed the ~858px title budget (content 936px − badge 58 − gap
    // 20) wrap to a 2nd line; the 60px box reserves room for both. minWidth:0 +
    // overflowWrap let the flex child shrink and wrap deterministically so
    // html2canvas can't resolve it to one overflowing line. NB: no
    // -webkit-line-clamp — html2canvas mis-measures the -webkit-box and clips.
    minWidth: 0,
    overflowWrap: "break-word",
  },
  shareHero: {
    // Natural flow (not flex-centered): html2canvas drifts on justify-content
    // centering. The margin-top:auto spacer below pins the ledger/footer to the
    // bottom so the hero sits directly under the identity with a clean gap.
    display: "flex",
    flexDirection: "column",
    paddingTop: "14px",
  },
  shareHeroEyebrow: {
    fontSize: "18px",
    fontWeight: 600,
    color: "#8d94a3",
    marginBottom: "6px",
    letterSpacing: 0,
  },
  shareHeroValue: {
    fontSize: "172px",
    fontWeight: 700,
    lineHeight: 1,
    color: "#f1f4fa",
    fontVariantNumeric: "tabular-nums",
    letterSpacing: "-0.03em",
    whiteSpace: "nowrap",
    margin: 0,
  },
  shareProof: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    marginTop: "20px",
  },
  shareDot: {
    width: "16px",
    height: "16px",
    flexShrink: 0,
  },
  shareProofRange: {
    fontSize: "31px",
    fontWeight: 600,
    color: "#f1f4fa",
    fontVariantNumeric: "tabular-nums",
  },
  shareProofSep: {
    fontSize: "31px",
    color: "#656c7b",
  },
  shareProofNote: {
    fontSize: "31px",
    fontWeight: 500,
    color: "#8d94a3",
  },
  shareLedger: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
  },
  shareCell: {
    padding: "22px 24px",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    boxSizing: "border-box",
  },
  shareCellLeftRule: {
    borderLeft: "1px solid rgba(220, 230, 245, 0.16)",
  },
  shareCellTopRule: {
    borderTop: "1px solid rgba(220, 230, 245, 0.16)",
  },
  shareCellLabel: {
    fontSize: "15px",
    fontWeight: 600,
    color: "#656c7b",
    letterSpacing: 0,
  },
  shareCellValue: {
    fontSize: "28px",
    fontWeight: 700,
    color: "#f1f4fa",
    fontVariantNumeric: "tabular-nums",
    display: "flex",
    alignItems: "center",
    gap: "11px",
    lineHeight: 1.1,
  },
  shareDotSm: {
    width: "13px",
    height: "13px",
    flexShrink: 0,
  },
  shareFooter: {
    marginTop: "26px",
    paddingTop: "22px",
    borderTop: "1px solid rgba(220, 230, 245, 0.16)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  shareDomain: {
    fontSize: "21px",
    fontWeight: 700,
    color: "#f1f4fa",
    letterSpacing: "-0.01em",
  },
  shareFooterMeta: {
    fontSize: "17px",
    fontWeight: 500,
    color: "#656c7b",
  },
};
